import React, { useState, useEffect } from 'react';
import FilterBar from './components/FilterBar';
import IntentSignalsTable, { IntentSignal } from './components/IntentSignalsTable';
import { useIntentSignal } from '../../../hooks/useIntentSignal';
import { useSidebar } from '../../../context/SidebarContext';
import { useRouter } from '@tanstack/react-router';
import EditIntent from './editIntent';
import { toast } from 'react-toastify';
import axios from 'axios';
import apiHandler from '../../../services/apiServices';
import { generateAndDownloadCSV } from '../../../utils/csvGenerator';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NewTourGuide from '../../../components/TourGuide/NewTourGuide';

interface IntentSignalsProps {
  isOpen?: boolean;
}

const IntentSignals: React.FC<IntentSignalsProps> = ({ isOpen = false }) => {
  const [selectedSignals, setSelectedSignals] = useState<ReturnType<typeof useIntentSignal>['intentSignals'][number][]>([]);
  const [selectedSignal, setSelectedSignal] = useState<IntentSignal | null>(null);
  const { isSidebarOpen } = useSidebar();
  const [isCreateIntentOpen, setIsCreateIntentOpen] = useState(false);
  const { 
    intentSignals,
    totalCount,
    isFetchingSignals,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
    sortOption,
    setSortOption
  } = useIntentSignal(20);
  const router = useRouter();
  const [isNavigating, setIsNavigating] = useState(false);
  const [searchResults, setSearchResults] = useState<IntentSignal[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isEditIntentOpen, setIsEditIntentOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const searchParams = new URLSearchParams(window.location.search);
      const openCreateIntent = searchParams.get('openCreateIntent');
      
      if (openCreateIntent && (openCreateIntent === 'true' || openCreateIntent === '"true"')) {
        setIsCreateIntentOpen(true);
        // Clean up the URL
        window.history.replaceState({}, '', window.location.pathname);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const path = window.location.pathname;
    const signalId = path.match(/\/intent-signals\/(.+)/)?.[1];
    
    if (signalId) {
      const signal = intentSignals.find(s => s._id === signalId);
      if (signal) {
        setSelectedSignal(signal);
        setIsEditIntentOpen(true);
      }
    }
  }, [intentSignals]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const startTour = searchParams.get('startTour');
    
    if (startTour && (startTour === 'true' || startTour === '"true"')) {
      // Clean up the URL first
      window.history.replaceState({}, '', window.location.pathname);
      // Show tour after a short delay to ensure components are mounted
      setTimeout(() => {
        setShowTour(true);
      }, 500);
    }
  }, []);

  const handleDownloadCSV = async () => {
    let signalsToDownload: IntentSignal[] = [];

    try {
      if (selectedSignals.length > 0) {
        signalsToDownload = selectedSignals;
      } else {
        const response = await apiHandler.get('/intent-signals?skip=0&limit=499');
        if (Array.isArray(response.data.data)) {
          signalsToDownload = response.data.data;
        } else {
          toast.error('Invalid data format received');
          return;
        }
      }

      if (signalsToDownload.length === 0) {
        toast.error('No signals available to download');
        return;
      }

      // Create CSV content
      const headers = ['Signal Description', 'Created On', 'Last Run', 'Status', 'Lead Count'];
      const rows = signalsToDownload.map(signal => [
        signal.signal,
        new Date(signal.created_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        new Date(signal.updated_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        signal.status,
        signal.engagement_count
      ]);

      generateAndDownloadCSV({
        headers,
        rows,
        filename: 'intent_signals.csv'
      });

    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED') {
          toast.error('Request timed out. Please try again.');
        } else {
          toast.error('Error downloading signals. Please try again.');
        }
      } else {
        toast.error('An unexpected error occurred');
      }
      console.error('Error:', error);
    }
  };

  const handleSignalSelect = (signal: IntentSignal) => {
    setSelectedSignal(signal);
  };

  const handleNavigate = async (direction: 'up' | 'down') => {
    setIsNavigating(true);
    const currentIndex = intentSignals.findIndex(s => s._id === selectedSignal?._id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex >= 0 && newIndex < intentSignals.length) {
      setSelectedSignal(intentSignals[newIndex]);
    }

    // If we're getting close to the end and there are more signals to fetch
    if (direction === 'down' && 
        newIndex >= intentSignals.length - 3 && 
        hasNextPage && 
        !isFetchingNextPage) {
      await fetchNextPage();
    }
    
    setIsNavigating(false);
  };

  const handleSortChange = (option: string) => {
    setSortOption(option);
    setSelectedSignals([]); // Clear selected signals when sort changes
  };

  const handleSearch = (results: IntentSignal[]) => {
    setSearchResults(results);
  };

  const handleCloseEditIntent = () => {
    setIsEditIntentOpen(false);
    setSelectedSignal(null);
    window.history.pushState({}, '', '/intent-signals');
  };

  const handleTourComplete = () => {
    setShowTour(false);
    // Optionally store in localStorage to prevent showing again
    localStorage.setItem('tourCompleted', 'true');
  };

  return (
    <>
      <div className={`flex flex-col transition-all duration-300 ${
        isSidebarOpen ? 'ml-[262px]' : 'ml-0'
      }`}>
        <div className="bg-[#FFFFFF]">
          <FilterBar 
            selectedSignals={selectedSignals}
            onDownloadCSV={handleDownloadCSV}
            isCreateIntentOpen={isCreateIntentOpen}
            setIsCreateIntentOpen={setIsCreateIntentOpen}
            onSortChange={handleSortChange}
            onSearch={handleSearch}
            totalCount={totalCount}
            onSearchQueryChange={setSearchQuery}
          />
        </div>
        <div className="flex items-center justify-center">
          <IntentSignalsTable 
            selectedSignals={selectedSignals}
            setSelectedSignals={setSelectedSignals}
            onSignalSelect={handleSignalSelect}
            sortOption={sortOption}
            intentSignals={searchResults.length > 0 ? searchResults : intentSignals}
            totalCount={totalCount}
            isFetchingSignals={isFetchingSignals}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            error={error}
            searchQuery={searchQuery}
          />
        </div>
      </div>

      <EditIntent
        isOpen={isEditIntentOpen}
        onClose={handleCloseEditIntent}
        signal={selectedSignal}
        currentIndex={selectedSignal ? intentSignals.findIndex(s => s._id === selectedSignal._id) : 0}
        totalCount={totalCount}
        onNavigate={handleNavigate}
        isNavigating={isNavigating}
        setIsNavigating={setIsNavigating}
        allSignals={intentSignals}
      />

      <NewTourGuide 
        isRunning={showTour} 
        onComplete={handleTourComplete}
      />

      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 10000 }}
      />
    </>
  );
};

export default IntentSignals;
